import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Package, Plus, Search, MapPin, Building2, User as UserIcon, Warehouse, Truck,
  Edit3, Trash2, Camera, FileText, ArrowRight, History, DollarSign, Filter,
  Upload, X, Image as ImageIcon, ChevronDown, MoreHorizontal, AlertTriangle,
  CheckCircle2, Boxes, Users, Layers,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

// ---------------------------------------------------- Constantes visuales
const CAT_LABEL = {
  portatil: "Portátil", monitor: "Monitor", teclado: "Teclado", raton: "Ratón",
  auriculares: "Auriculares", silla: "Silla", mesa: "Mesa", movil: "Móvil",
  tablet: "Tablet", impresora: "Impresora", camara: "Cámara", router: "Router",
  tarjeta_acceso: "Tarjeta acceso", software: "Software",
  material_oficina: "Material oficina", otros: "Otros",
};
const STATUS_LABEL = {
  nuevo: "Nuevo", usado: "En uso", reparacion: "En reparación",
  prestamo: "En préstamo", baja: "Dado de baja", extraviado: "Extraviado",
};
const STATUS_STYLE = {
  nuevo: "bg-emerald-100 text-emerald-800 border-emerald-300",
  usado: "bg-blue-100 text-blue-800 border-blue-300",
  reparacion: "bg-amber-100 text-amber-800 border-amber-300",
  prestamo: "bg-purple-100 text-purple-800 border-purple-300",
  baja: "bg-slate-200 text-slate-600 border-slate-300 line-through",
  extraviado: "bg-rose-100 text-rose-800 border-rose-300",
};
const KIND_ICON = { empleado: UserIcon, oficina: Building2, almacen: Warehouse, externo: Truck, otro: MapPin };
const eur = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n));

// ---------------------------------------------------- KPI Card
function Kpi({ icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "bg-white",
    primary: "bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30",
    warn: "bg-gradient-to-br from-amber-100 to-amber-50 border-amber-200",
    good: "bg-gradient-to-br from-emerald-100 to-emerald-50 border-emerald-200",
  };
  return (
    <Card className={`overflow-hidden ${tones[tone]}`}>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`grid h-11 w-11 place-items-center rounded-lg ${tone === "primary" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold leading-none">{value}</p>
          {sub && <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------- Location option (para Selects)
function useLocationsFlat() {
  const { data: locs } = useQuery({
    queryKey: ["inv-locations"],
    queryFn: async () => (await api.get("/inventario/locations")).data,
  });
  const flat = useMemo(() => {
    const out = [];
    (locs?.manual || []).forEach((l) => out.push({ id: l.id, name: l.name, kind: l.kind || "otro", manual: true, item_count: l.item_count || 0 }));
    (locs?.employees || []).forEach((e) => out.push({ id: e.id, name: e.name, kind: "empleado", manual: false, item_count: e.item_count || 0, employee_email: e.employee_email }));
    return out;
  }, [locs]);
  return { locs, flat };
}

// ---------------------------------------------------- Item Editor (crear / editar)
function ItemEditor({ open, onClose, item, onSaved }) {
  const qc = useQueryClient();
  const isEdit = !!item?.id;
  const { flat } = useLocationsFlat();
  const [form, setForm] = useState(() => ({
    name: item?.name || "",
    category: item?.category || "otros",
    serial: item?.serial || "",
    status: item?.status || "usado",
    location_id: item?.location_id || "",
    purchase_date: item?.purchase_date || "",
    cost_eur: item?.cost_eur ?? "",
    notes: item?.notes || "",
    _movement_note: "",
  }));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const mut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name, category: form.category, serial: form.serial, status: form.status,
        location_id: form.location_id || null, notes: form.notes,
        purchase_date: form.purchase_date || null,
        cost_eur: form.cost_eur === "" ? null : Number(form.cost_eur),
      };
      if (isEdit && form._movement_note) payload._movement_note = form._movement_note;
      const url = isEdit ? `/inventario/items/${item.id}` : "/inventario/items";
      const method = isEdit ? "patch" : "post";
      return (await api[method](url, payload)).data;
    },
    onSuccess: (data) => {
      toast.success(isEdit ? "Artículo actualizado" : "Artículo creado");
      qc.invalidateQueries({ queryKey: ["inv-items"] });
      qc.invalidateQueries({ queryKey: ["inv-summary"] });
      qc.invalidateQueries({ queryKey: ["inv-locations"] });
      onSaved?.(data);
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inv-item-editor">
        <DialogHeader><DialogTitle>{isEdit ? "Editar artículo" : "Nuevo artículo"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nombre *</label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="MacBook Pro 14, Silla ergonómica…" data-testid="inv-name" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Categoría</label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="inv-category"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CAT_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Estado</label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="inv-status"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nº de serie</label>
            <Input value={form.serial} onChange={(e) => set("serial")(e.target.value)} placeholder="C02XXXXXX" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fecha de compra</label>
            <Input type="date" value={form.purchase_date || ""} onChange={(e) => set("purchase_date")(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Coste (€)</label>
            <Input type="number" step="0.01" value={form.cost_eur} onChange={(e) => set("cost_eur")(e.target.value)} placeholder="0.00" />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ubicación</label>
            <Select value={form.location_id || "__none__"} onValueChange={(v) => set("location_id")(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="inv-location"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin asignar</SelectItem>
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Empleados</div>
                {flat.filter((l) => l.kind === "empleado").map((l) => <SelectItem key={l.id} value={l.id}>👤 {l.name}</SelectItem>)}
                <div className="mt-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ubicaciones</div>
                {flat.filter((l) => l.kind !== "empleado").map((l) => <SelectItem key={l.id} value={l.id}>📍 {l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isEdit && form.location_id !== (item?.location_id || "") && (
            <div className="col-span-2">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Motivo del movimiento (opcional)</label>
              <Input value={form._movement_note} onChange={(e) => set("_movement_note")(e.target.value)} placeholder="Ej. Nueva asignación por incorporación" />
            </div>
          )}
          <div className="col-span-2">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notas</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Cualquier detalle relevante…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!form.name || mut.isPending} data-testid="inv-save">
            {mut.isPending ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear artículo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------- Item Detail (con historial y adjuntos)
function ItemDetail({ open, onClose, itemId, onEdit }) {
  const qc = useQueryClient();
  const { data: it, isLoading } = useQuery({
    queryKey: ["inv-item", itemId],
    queryFn: async () => (await api.get(`/inventario/items/${itemId}`)).data,
    enabled: !!itemId && open,
  });

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/inventario/items/${itemId}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(kind === "photo" ? "Foto subida" : "Factura subida");
      qc.invalidateQueries({ queryKey: ["inv-item", itemId] });
      qc.invalidateQueries({ queryKey: ["inv-items"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo subir"); }
  };

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/inventario/items/${itemId}`)).data,
    onSuccess: () => { toast.success("Artículo dado de baja"); qc.invalidateQueries({ queryKey: ["inv-items"] }); qc.invalidateQueries({ queryKey: ["inv-summary"] }); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo dar de baja"),
  });

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="inv-item-detail">
        {isLoading || !it ? <Skeleton className="h-72 w-full" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />{it.name}
                <Badge className={STATUS_STYLE[it.status] || ""}>{STATUS_LABEL[it.status] || it.status}</Badge>
              </DialogTitle>
              <p className="text-xs text-muted-foreground">{CAT_LABEL[it.category] || it.category} · {it.serial || "sin serie"}</p>
            </DialogHeader>
            <div className="grid grid-cols-[130px_1fr] gap-4">
              {/* Foto */}
              <div>
                {it.photo_file_id ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/inventario/items/${it.id}/photo`}
                       alt={it.name} className="h-32 w-32 rounded-md border object-cover" data-testid="inv-photo" />
                ) : (
                  <label className="grid h-32 w-32 cursor-pointer place-items-center rounded-md border border-dashed text-center text-[10px] text-muted-foreground hover:bg-muted">
                    <div>
                      <Camera className="mx-auto mb-1 h-5 w-5" />Subir foto
                    </div>
                    <input type="file" accept="image/*" hidden onChange={(e) => upload("photo", e.target.files?.[0])} data-testid="inv-photo-input" />
                  </label>
                )}
                {it.photo_file_id && (
                  <label className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[10px] text-primary hover:underline">
                    <Upload className="h-3 w-3" />Reemplazar foto
                    <input type="file" accept="image/*" hidden onChange={(e) => upload("photo", e.target.files?.[0])} />
                  </label>
                )}
              </div>
              {/* Datos */}
              <div className="space-y-1.5 text-sm">
                <p><strong>Ubicación:</strong> {it.location?.name || "Sin asignar"} <span className="text-[10px] text-muted-foreground">({it.location?.kind || "—"})</span></p>
                <p><strong>Fecha compra:</strong> {it.purchase_date || "—"}</p>
                <p><strong>Coste:</strong> <span className="font-semibold">{eur(it.cost_eur)}</span></p>
                {it.notes && <p className="rounded-md bg-muted p-2 text-xs">{it.notes}</p>}
                {/* Factura */}
                <div className="flex items-center gap-2 pt-1">
                  {it.invoice_file_id ? (
                    <>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/inventario/items/${it.id}/invoice`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="inv-invoice-link">
                        <FileText className="h-4 w-4" />Ver factura PDF
                      </a>
                      <label className="ml-2 inline-flex cursor-pointer items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground">
                        <Upload className="h-3 w-3" />Reemplazar
                        <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => upload("invoice", e.target.files?.[0])} />
                      </label>
                    </>
                  ) : (
                    <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-dashed px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted" data-testid="inv-invoice-btn">
                      <FileText className="h-3.5 w-3.5" />Adjuntar factura
                      <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => upload("invoice", e.target.files?.[0])} />
                    </label>
                  )}
                </div>
              </div>
            </div>
            {/* Historial */}
            <div className="mt-3">
              <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <History className="h-3 w-3" />Historial de movimientos ({(it.history || []).length})
              </p>
              <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border p-2 text-xs">
                {(it.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="flex gap-2 border-b border-dashed py-1 last:border-b-0">
                    <span className="w-32 shrink-0 text-[10px] text-muted-foreground">{new Date(h.at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</span>
                    <div className="min-w-0 flex-1">
                      {h.action === "create" && <p>➕ <strong>Creado</strong>{h.to?.name ? <> — asignado a <em>{h.to.name}</em></> : ""}</p>}
                      {h.action === "move"   && <p>🔀 <strong>Movido</strong> de <em>{h.from?.name || "—"}</em> → <em>{h.to?.name || "—"}</em></p>}
                      {h.action === "status" && <p>🎯 <strong>Estado</strong> {h.from || "—"} → {h.to}</p>}
                      {h.note && <p className="text-[10.5px] text-muted-foreground italic">{h.note}</p>}
                      <p className="text-[9.5px] text-muted-foreground">por {h.by}</p>
                    </div>
                  </div>
                ))}
                {(it.history || []).length === 0 && <p className="text-center text-muted-foreground">Sin movimientos.</p>}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => confirm("¿Dar de baja este artículo?") && del.mutate()} data-testid="inv-baja">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />Dar de baja
              </Button>
              <Button onClick={() => { onEdit(it); }} data-testid="inv-edit-open">
                <Edit3 className="mr-1.5 h-3.5 w-3.5" />Editar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------- Location Editor
function LocationEditor({ open, onClose, loc }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({ name: loc?.name || "", kind: loc?.kind || "oficina", notes: loc?.notes || "" }));
  const isEdit = !!loc?.id;
  const mut = useMutation({
    mutationFn: async () => {
      const url = isEdit ? `/inventario/locations/${loc.id}` : "/inventario/locations";
      return (await api[isEdit ? "patch" : "post"](url, form)).data;
    },
    onSuccess: () => { toast.success(isEdit ? "Ubicación actualizada" : "Ubicación creada"); qc.invalidateQueries({ queryKey: ["inv-locations"] }); onClose(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="inv-loc-editor">
        <DialogHeader><DialogTitle>{isEdit ? "Editar ubicación" : "Nueva ubicación"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Nombre *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Oficina Madrid, Almacén Getafe…" data-testid="inv-loc-name" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tipo</label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="inv-loc-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oficina">Oficina</SelectItem>
                <SelectItem value="almacen">Almacén</SelectItem>
                <SelectItem value="externo">Externo (envío, cliente, reparación…)</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notas</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!form.name || mut.isPending} data-testid="inv-loc-save">{mut.isPending ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------- Página principal
export default function InventarioPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [locFilter, setLocFilter] = useState("__all__");
  const [includeBaja, setIncludeBaja] = useState(false);
  const [openItem, setOpenItem] = useState(null); // creating/editing item
  const [openDetailId, setOpenDetailId] = useState(null);
  const [openLoc, setOpenLoc] = useState(null); // creating/editing location

  const { data: summary } = useQuery({
    queryKey: ["inv-summary"],
    queryFn: async () => (await api.get("/inventario/summary")).data,
  });
  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["inv-items", q, category, statusFilter, locFilter, includeBaja],
    queryFn: async () => (await api.get("/inventario/items", {
      params: {
        q: q || undefined,
        category: category === "__all__" ? undefined : category,
        status_filter: statusFilter === "__all__" ? undefined : statusFilter,
        location_id: locFilter === "__all__" ? undefined : locFilter,
        include_baja: includeBaja || undefined,
      },
    })).data,
  });
  const { locs, flat } = useLocationsFlat();

  const items = itemsData?.items || [];
  // Agrupar por ubicación para vista "Por empleado" / "Por ubicación"
  const byLocation = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      const key = it.location_id || "__none__";
      if (!m.has(key)) m.set(key, { location: it.location, id: key, items: [] });
      m.get(key).items.push(it);
    });
    return Array.from(m.values()).sort((a, b) => b.items.length - a.items.length);
  }, [items]);

  const delLoc = async (id) => {
    if (!confirm("¿Borrar esta ubicación? (solo si no tiene artículos)")) return;
    try { await api.delete(`/inventario/locations/${id}`); toast.success("Ubicación borrada"); qc.invalidateQueries({ queryKey: ["inv-locations"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  return (
    <div className="space-y-4" data-testid="inventario-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <Package className="h-6 w-6 text-primary" />Inventario
          </h1>
          <p className="text-sm text-muted-foreground">Herramienta de <strong>Dirección</strong>: materiales por empleado, oficina, almacén o cualquier otra ubicación.</p>
        </div>
        <Button onClick={() => setOpenItem({})} data-testid="inv-new-item"><Plus className="mr-1.5 h-4 w-4" />Nuevo artículo</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={Boxes}  label="Total artículos"  value={summary?.total ?? "—"} sub={`${summary?.baja || 0} dados de baja`} tone="primary" />
        <Kpi icon={DollarSign} label="Valor inventario" value={eur(summary?.valor_total)} sub="Suma de coste (excl. bajas)" tone="good" />
        <Kpi icon={Layers} label="Categorías activas" value={Object.keys(summary?.by_category || {}).length} sub={Object.entries(summary?.by_category || {}).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,n])=>`${CAT_LABEL[k]||k} ${n}`).join(" · ")} />
        <Kpi icon={Users}  label="Empleados con material" value={summary?.top_empleados?.length ?? 0} sub={summary?.top_empleados?.[0] ? `Top: ${summary.top_empleados[0].name} (${summary.top_empleados[0].n})` : "—"} tone="warn" />
      </div>

      <Tabs defaultValue="listado" className="w-full">
        <TabsList data-testid="inv-tabs">
          <TabsTrigger value="listado" data-testid="tab-listado">Listado</TabsTrigger>
          <TabsTrigger value="empleados" data-testid="tab-empleados">Por empleado</TabsTrigger>
          <TabsTrigger value="ubicaciones" data-testid="tab-ubicaciones">Ubicaciones</TabsTrigger>
        </TabsList>

        {/* ---------------- LISTADO --------------- */}
        <TabsContent value="listado" className="space-y-3">
          <Card><CardContent className="p-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
              <div className="relative md:col-span-2">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, serie, notas…" className="pl-8" data-testid="inv-search" />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="inv-filter-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las categorías</SelectItem>
                  {Object.entries(CAT_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="inv-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los estados</SelectItem>
                  {Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={locFilter} onValueChange={setLocFilter}>
                <SelectTrigger data-testid="inv-filter-location"><SelectValue placeholder="Ubicación" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las ubicaciones</SelectItem>
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Empleados</div>
                  {flat.filter((l) => l.kind === "empleado" && l.item_count > 0).map((l) => <SelectItem key={l.id} value={l.id}>👤 {l.name} ({l.item_count})</SelectItem>)}
                  <div className="mt-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ubicaciones</div>
                  {flat.filter((l) => l.kind !== "empleado").map((l) => <SelectItem key={l.id} value={l.id}>📍 {l.name} ({l.item_count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input type="checkbox" checked={includeBaja} onChange={(e) => setIncludeBaja(e.target.checked)} data-testid="inv-toggle-baja" /> Incluir dados de baja
            </label>
          </CardContent></Card>

          {isLoading ? <Skeleton className="h-64 w-full" /> : (
            <Card><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inv-items-table">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Artículo</th>
                      <th className="px-3 py-2">Categoría</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Ubicación</th>
                      <th className="px-3 py-2 text-right">Coste</th>
                      <th className="px-3 py-2">Compra</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No hay artículos. Pulsa <strong>Nuevo artículo</strong> para empezar.</td></tr>}
                    {items.map((it) => {
                      const KindIcon = KIND_ICON[it.location?.kind] || MapPin;
                      return (
                        <tr key={it.id} className="border-b hover:bg-muted/30" data-testid={`inv-row-${it.id}`}>
                          <td className="px-3 py-2">
                            <button className="text-left font-medium hover:underline" onClick={() => setOpenDetailId(it.id)} data-testid={`inv-open-${it.id}`}>
                              {it.name}
                            </button>
                            {it.serial && <p className="text-[10px] text-muted-foreground">SN: {it.serial}</p>}
                          </td>
                          <td className="px-3 py-2 text-xs">{CAT_LABEL[it.category] || it.category}</td>
                          <td className="px-3 py-2"><Badge className={STATUS_STYLE[it.status] || ""}>{STATUS_LABEL[it.status] || it.status}</Badge></td>
                          <td className="px-3 py-2 text-xs"><span className="inline-flex items-center gap-1"><KindIcon className="h-3.5 w-3.5 text-muted-foreground" />{it.location?.name || "—"}</span></td>
                          <td className="px-3 py-2 text-right text-xs">{eur(it.cost_eur)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{it.purchase_date || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpenDetailId(it.id)} data-testid={`inv-detail-${it.id}`}>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ---------------- POR EMPLEADO --------------- */}
        <TabsContent value="empleados" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {(flat.filter((l) => l.kind === "empleado").sort((a,b) => b.item_count - a.item_count)).map((emp) => {
              const empItems = items.filter((i) => i.location_id === emp.id);
              const total = empItems.reduce((s, i) => s + (i.cost_eur || 0), 0);
              return (
                <Card key={emp.id} data-testid={`inv-emp-card-${emp.id}`}>
                  <CardContent className="p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary"><UserIcon className="h-4 w-4" /></div>
                        <div>
                          <p className="text-sm font-semibold">{emp.name}</p>
                          <p className="text-[10px] text-muted-foreground">{emp.employee_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold leading-none">{emp.item_count}</p>
                        <p className="text-[10px] text-muted-foreground">artículos</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {empItems.slice(0, 5).map((it) => (
                        <button key={it.id} onClick={() => setOpenDetailId(it.id)} className="flex w-full items-center justify-between rounded-md border px-2 py-1 text-xs hover:bg-muted">
                          <span className="truncate">{it.name}</span>
                          <Badge className={STATUS_STYLE[it.status]}>{STATUS_LABEL[it.status]}</Badge>
                        </button>
                      ))}
                      {emp.item_count > 5 && <p className="text-[10px] text-muted-foreground">+ {emp.item_count - 5} más…</p>}
                      {emp.item_count === 0 && <p className="text-xs italic text-muted-foreground">Sin material asignado.</p>}
                    </div>
                    {total > 0 && <p className="mt-2 text-right text-[11px] text-muted-foreground">Valor total: <strong>{eur(total)}</strong></p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* ---------------- UBICACIONES --------------- */}
        <TabsContent value="ubicaciones" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => setOpenLoc({})} data-testid="inv-new-loc"><Plus className="mr-1.5 h-4 w-4" />Nueva ubicación</Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {(locs?.manual || []).map((l) => {
              const KindIcon = KIND_ICON[l.kind] || MapPin;
              return (
                <Card key={l.id} data-testid={`inv-loc-card-${l.id}`}>
                  <CardContent className="p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <KindIcon className="h-4 w-4 text-primary" />
                        <p className="font-semibold">{l.name}</p>
                      </div>
                      <Badge variant="secondary">{l.item_count} art.</Badge>
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l.kind}</p>
                    {l.notes && <p className="mt-1 text-xs text-muted-foreground">{l.notes}</p>}
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setOpenLoc(l)} data-testid={`inv-loc-edit-${l.id}`}><Edit3 className="mr-1 h-3 w-3" />Editar</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:bg-rose-50" onClick={() => delLoc(l.id)} data-testid={`inv-loc-del-${l.id}`}><Trash2 className="mr-1 h-3 w-3" />Borrar</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {(locs?.manual || []).length === 0 && (
              <Card className="col-span-full border-dashed">
                <CardContent className="p-6 text-center text-muted-foreground">
                  Todavía no has creado ninguna ubicación. Los empleados ya aparecen automáticamente como ubicaciones.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {openItem && <ItemEditor open={true} item={openItem?.id ? openItem : null} onClose={() => setOpenItem(null)} onSaved={(data) => data?.id && setOpenDetailId(data.id)} />}
      {openDetailId && <ItemDetail open={true} itemId={openDetailId} onClose={() => setOpenDetailId(null)} onEdit={(it) => { setOpenDetailId(null); setOpenItem(it); }} />}
      {openLoc && <LocationEditor open={true} loc={openLoc?.id ? openLoc : null} onClose={() => setOpenLoc(null)} />}
    </div>
  );
}
